import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Anchor } from 'lucide-react';
import { yachtServices } from '../data/mock';
import { Button } from './ui/button';

export const YachtsSection = ({ onNavigate }) => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-stone-900 relative overflow-hidden" id="yachts">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-full mb-6">
            <Anchor className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-400 tracking-widest uppercase">Maritime Excellence</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-serif text-white mb-4">
            {t('yachts.title')}
          </h2>
          <p className="text-lg text-stone-400 max-w-2xl mx-auto">
            {t('yachts.subtitle')}
          </p>
        </motion.div>

        {/* Yachts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {yachtServices.map((yacht, index) => (
            <motion.div
              key={yacht.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="group"
            >
              <div className="bg-stone-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-stone-700/50 hover:border-amber-500/30 transition-all duration-300">
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={yacht.image}
                    alt={yacht.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/20 to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-serif text-white mb-3">
                    {yacht.title}
                  </h3>
                  <p className="text-stone-400 text-sm leading-relaxed mb-4">
                    {yacht.description}
                  </p>
                  
                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    {yacht.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-stone-300">
                        <Check className="w-4 h-4 text-amber-400" />
                        {feature}
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => onNavigate('contact')}
                    className="w-full bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:border-amber-500/50 transition-all duration-300"
                  >
                    {t('yachts.inquire')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
